import { useEffect, useRef, useState } from 'react';

export interface ScrollState {
  /** Past 30 px: the header lifts. */
  scrolled: boolean;
  /** Past 560 px: the home header swaps the switch for the search pill. */
  deep: boolean;
  /** Scrolling down past 140 px: the bottom bar shrinks. */
  mini: boolean;
}

/**
 * Mirrors the mockup scroll behaviour, with a rAF throttle. A new page scrolls to the top,
 * which fires a scroll event and resets the state.
 */
export function useScrollState(): ScrollState {
  const [state, setState] = useState<ScrollState>({ scrolled: false, deep: false, mini: false });
  const lastY = useRef(0);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = window.scrollY;
        setState((s) => {
          const next = { ...s, scrolled: y > 30, deep: y > 560 };
          const dy = y - lastY.current;
          if (Math.abs(dy) > 10) {
            next.mini = dy > 0 && y > 140;
            lastY.current = y;
          }
          if (y < 140) next.mini = false;
          return next.scrolled === s.scrolled && next.deep === s.deep && next.mini === s.mini ? s : next;
        });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return state;
}
